import React, {useState, useEffect, useRef} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    IconButton,
    Avatar,
    Chip,
    CircularProgress,
    Alert,
    Fade
} from '@mui/material';
import {
    Close as CloseIcon,
    Send as SendIcon,
    SmartToy as TutorIcon,
    Person as StudentIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
// Math and Markdown rendering imports
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import {createGamificationService} from '../../../services/GamificationService';

// Markdown and Math component with KaTeX support
const MathText = ({children}) => {
    return (
        <ReactMarkdown
            remarkPlugins={[remarkMath, remarkGfm]}
            rehypePlugins={[rehypeKatex]}
            components={{
                // Customize code blocks for better styling
                code({node, inline, className, children, ...props}) {
                    return inline ? (
                        <code
                            style={{
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontSize: '0.9em'
                            }}
                            {...props}
                        >
                            {children}
                        </code>
                    ) : (
                        <code
                            style={{
                                display: 'block',
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                padding: '8px',
                                borderRadius: '4px',
                                fontSize: '0.9em',
                                overflowX: 'auto'
                            }}
                            {...props}
                        >
                            {children}
                        </code>
                    );
                },
                // Customize table styling
                table({node, children, ...props}) {
                    return (
                        <table
                            style={{
                                borderCollapse: 'collapse',
                                width: '100%',
                                marginTop: '8px',
                                marginBottom: '8px',
                                fontSize: '0.9em'
                            }}
                            {...props}
                        >
                            {children}
                        </table>
                    );
                },
                thead({node, children, ...props}) {
                    return (
                        <thead
                            style={{
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                borderBottom: '2px solid rgba(0, 0, 0, 0.2)'
                            }}
                            {...props}
                        >
                            {children}
                        </thead>
                    );
                },
                th({node, children, ...props}) {
                    return (
                        <th
                            style={{
                                padding: '8px',
                                textAlign: 'left',
                                fontWeight: 'bold',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                            }}
                            {...props}
                        >
                            {children}
                        </th>
                    );
                },
                td({node, children, ...props}) {
                    return (
                        <td
                            style={{
                                padding: '8px',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
                            }}
                            {...props}
                        >
                            {children}
                        </td>
                    );
                },
                tr({node, children, ...props}) {
                    return (
                        <tr
                            style={{
                                borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
                            }}
                            {...props}
                        >
                            {children}
                        </tr>
                    );
                }
            }}
        >
            {children}
        </ReactMarkdown>
    );
};

const ChatInterface = ({
                           open,
                           onClose,
                           question,
                           answer,
                           accessToken
                       }) => {
    // State management
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);

    // AI Tutor unlock state (per question)
    const [unlockedForThisQuestion, setUnlockedForThisQuestion] = useState(false);
    const [unlocking, setUnlocking] = useState(false);
    const [unlockError, setUnlockError] = useState(null);
    const [userCoins, setUserCoins] = useState(null);

    // Refs
    const messagesEndRef = useRef(null);
    const eventSourceRef = useRef(null);

    // Auto-scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Check user coins on mount
    useEffect(() => {
        const fetchUserCoins = async () => {
            if (!accessToken) return;
            const gamification = createGamificationService(accessToken);
            try {
                const stats = await gamification.getUserStats();
                setUserCoins(stats.total_coins);
            } catch (e) {
                setUserCoins(null);
            }
        };
        if (open) {
            fetchUserCoins();
        }
    }, [accessToken, open]);

    // Show initial system message when component opens (only if unlocked for this question)
    useEffect(() => {
        if (open && question && answer && messages.length === 0 && unlockedForThisQuestion) {
            const systemMessage = {
                id: Date.now(),
                type: 'system',
                content: `Frag mich alles zur Frage: "${question.text}"\n\nDeine Antwort war: ${getAnswerText(answer, question)}\nErgebnis: ${answer.is_correct ? '✅ Richtig' : '❌ Falsch'} (${answer.points_earned || 0}/${answer.max_points || 1} Punkte)`
            };
            setMessages([systemMessage]);
        }
    }, [open, question, answer, unlockedForThisQuestion]);

    // AI Tutor unlock function (per question)
    const handleUnlock = async () => {
        setUnlocking(true);
        setUnlockError(null);
        const gamification = createGamificationService(accessToken);
        try {
            const res = await gamification.unlockAiTutor();
            setUserCoins(res.remaining_coins);
            setUnlockedForThisQuestion(true);
        } catch (e) {
            setUnlockError(e.message || 'Error unlocking AI Tutor');
        } finally {
            setUnlocking(false);
        }
    };

    // Cleanup when component unmounts or closes
    useEffect(() => {
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, []);

    // Reset state when dialog closes
    const handleClose = () => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }
        setMessages([]);
        setInputValue('');
        setError(null);
        setIsLoading(false);
        setIsStreaming(false);
        setUnlockError(null);
        // Reset unlock state - user needs to pay again for each question
        setUnlockedForThisQuestion(false);
        onClose();
    };

    // Send question to tutor (handles both initial and follow-up questions)
    const sendMessage = async () => {
        if (!inputValue.trim() || isStreaming) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: inputValue.trim()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);
        setError(null);
        setIsStreaming(true);

        try {
            // Check if this is the first question or a follow-up
            const chatHistory = messages.filter(msg => msg.type === 'user' || msg.type === 'tutor');
            await streamTutorResponse(userMessage.content, chatHistory);
        } catch (err) {
            console.error('Error sending message:', err);
            setError('Failed to send message. Please try again.');
        } finally {
            setIsLoading(false);
            setIsStreaming(false);
        }
    };

    // Stream tutor response using Server-Sent Events
    const streamTutorResponse = async (userQuestion, chatHistory = []) => {
        const baseUrl = import.meta.env.VITE_API_URL;
        const url = `${baseUrl}/api/ai-tutor/chat-help`;

        // Build additional context from chat history
        let additionalQuestion = userQuestion;

        if (chatHistory.length > 0) {
            // Include previous conversation context
            const conversationContext = chatHistory
                .map(msg => `${msg.type === 'user' ? 'Student' : 'Tutor'}: ${msg.content}`)
                .join('\n\n');

            additionalQuestion = `Vorherige Unterhaltung:\n${conversationContext}\n\nNeue Frage: ${userQuestion}`;
        }

        const requestBody = {
            question_id: question.id,
            answer_id: answer.id,
            additional_question: additionalQuestion
        };

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                    'Accept': 'text/event-stream'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            // Create initial tutor message
            const tutorMessageId = Date.now() + 1;
            const tutorMessage = {
                id: tutorMessageId,
                type: 'tutor',
                content: '',
                streaming: true
            };

            setMessages(prev => [...prev, tutorMessage]);

            let buffer = '';

            while (true) {
                const {done, value} = await reader.read();

                if (done) break;

                buffer += decoder.decode(value, {stream: true});
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep incomplete line in buffer

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.slice(6); // Remove 'data: ' prefix
                            const data = JSON.parse(jsonStr);

                            if (data.type === 'content') {
                                // Update the streaming message content
                                setMessages(prev => prev.map(msg =>
                                    msg.id === tutorMessageId
                                        ? {...msg, content: msg.content + data.message}
                                        : msg
                                ));
                            } else if (data.type === 'complete') {
                                // Mark message as complete
                                setMessages(prev => prev.map(msg =>
                                    msg.id === tutorMessageId
                                        ? {...msg, streaming: false}
                                        : msg
                                ));
                                return; // Exit the function
                            } else if (data.type === 'error') {
                                throw new Error(data.message);
                            }
                        } catch (parseError) {
                            console.warn('Failed to parse SSE data:', parseError);
                        }
                    }
                }
            }
        } catch (err) {
            console.error('Streaming error:', err);
            setError(err.message || 'Connection error occurred');

            // Remove the incomplete streaming message
            setMessages(prev => prev.filter(msg => !msg.streaming));
        }
    };

    // Get readable answer text
    const getAnswerText = (answer, question) => {
        const userAnswer = answer.value || answer.value_text || answer.value_json;

        if (question.type === 'single_choice' || question.type === 'multi_choice') {
            const selectedValues = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
            const selectedOptions = question.options?.filter(opt =>
                selectedValues.includes(opt.value)
            ) || [];

            return selectedOptions.map(opt => opt.label || opt.text).join(', ') || 'Keine Auswahl';
        }

        return userAnswer || 'Keine Antwort gegeben';
    };

    // Handle Enter key press
    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendMessage();
        }
    };

    // Retry functionality
    const handleRetry = () => {
        setMessages([]);
        setError(null);
        // Show initial system message again if unlocked for this question
        if (question && answer && unlockedForThisQuestion) {
            const systemMessage = {
                id: Date.now(),
                type: 'system',
                content: `Frag mich alles zur Frage: "${question.text}"\n\nDeine Antwort war: ${getAnswerText(answer, question)}\nErgebnis: ${answer.is_correct ? '✅ Richtig' : '❌ Falsch'} (${answer.points_earned || 0}/${answer.max_points || 1} Punkte)`
            };
            setMessages([systemMessage]);
        }
    };

    // Render individual message
    const renderMessage = (message) => {
        const isSystem = message.type === 'system';
        const isTutor = message.type === 'tutor';
        const isUser = message.type === 'user';

        return (
            <Box
                key={message.id}
                sx={{
                    display: 'flex',
                    mb: 2,
                    flexDirection: isUser ? 'row-reverse' : 'row',
                    alignItems: 'flex-start'
                }}
            >
                {!isSystem && (
                    <Avatar
                        sx={{
                            bgcolor: isTutor ? 'primary.main' : 'secondary.main',
                            mr: isUser ? 0 : 1,
                            ml: isUser ? 1 : 0,
                            width: 32,
                            height: 32
                        }}
                    >
                        {isTutor ? <TutorIcon fontSize="small"/> : <StudentIcon fontSize="small"/>}
                    </Avatar>
                )}

                <Paper
                    elevation={1}
                    sx={{
                        p: 2,
                        maxWidth: '80%',
                        bgcolor: (theme) => isSystem
                            ? (theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.08)')
                            : isUser
                                ? (theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.08)')
                                : (theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.08)'),
                        position: 'relative',
                        boxShadow: (theme) => theme.palette.mode === 'dark'
                            ? '0 2px 8px rgba(0,0,0,0.3)'
                            : '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                >
                    {isSystem && (
                        <Chip
                            label="Fragen-Kontext"
                            size="small"
                            color="info"
                            sx={{mb: 1, borderRadius: 4}}
                        />
                    )}

                    <Typography variant="body2" component="div">
                        <MathText>{message.content}</MathText>
                    </Typography>

                    {message.streaming && (
                        <Box sx={{display: 'flex', alignItems: 'center', mt: 1}}>
                            <CircularProgress size={12} sx={{mr: 1}}/>
                            <Typography variant="caption" color="text.secondary">
                                Tutor tippt...
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
        );
    };

    if (!question || !answer) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    height: '80vh',
                    maxHeight: 600,
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? '0 12px 48px rgba(0,0,0,0.5)'
                        : '0 12px 48px rgba(0,0,0,0.15)',
                    backgroundColor: 'background.paper'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1,
                background: (theme) => theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                    : 'linear-gradient(135deg, #f8fafe 0%, #ffffff 100%)',
                borderBottom: '1px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'grey.100'
            }}>
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <TutorIcon sx={{mr: 1, color: 'primary.main'}}/>
                    <Typography variant="h6" color="text.primary">KI-Tutor Chat</Typography>
                </Box>
                <Box>
                    {error && (
                        <IconButton
                            onClick={handleRetry}
                            size="small"
                            sx={{mr: 1}}
                            title="Erneut versuchen"
                        >
                            <RefreshIcon/>
                        </IconButton>
                    )}
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon/>
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                px: 2,
                pb: 1
            }}>
                {!unlockedForThisQuestion ? (
                    // AI Tutor Unlock Screen
                    <Fade in timeout={1000}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 300,
                            textAlign: 'center',
                            p: 3
                        }}>
                            <TutorIcon sx={{fontSize: 80, color: 'primary.main', mb: 2}}/>
                            <Typography variant="h5" sx={{fontWeight: 'bold', mb: 2, color: 'text.primary'}}>
                                Hol dir KI-Hilfe für diese Frage
                            </Typography>
                            <Typography variant="body1" sx={{mb: 3, color: 'text.secondary', maxWidth: 400}}>
                                Erhalte personalisierte KI-Unterstützung für diese spezifische Frage!
                                Bekomme detaillierte Erklärungen und Hinweise, um die Lösung besser zu verstehen.
                            </Typography>
                            <Chip
                                label={`Verfügbare Coins: ${userCoins ?? '--'}`}
                                sx={{
                                    fontWeight: 'bold',
                                    background: (theme) => theme.palette.mode === 'dark'
                                        ? 'linear-gradient(45deg, #B8860B, #DAA520)'
                                        : 'linear-gradient(45deg, #FFD700, #FFA500)',
                                    color: (theme) => theme.palette.mode === 'dark' ? 'black' : 'white',
                                    mb: 3,
                                    borderRadius: 4
                                }}
                            />
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleUnlock}
                                disabled={unlocking || userCoins < 10}
                                sx={{
                                    borderRadius: 4,
                                    fontWeight: 'bold',
                                    fontSize: 18,
                                    px: 4,
                                    py: 1.5,
                                    background: (theme) => theme.palette.mode === 'dark'
                                        ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
                                        : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                                    boxShadow: (theme) => theme.palette.mode === 'dark'
                                        ? '0 4px 16px rgba(25, 118, 210, 0.4)'
                                        : '0 4px 16px rgba(25, 118, 210, 0.3)',
                                    '&:hover': {
                                        background: (theme) => theme.palette.mode === 'dark'
                                            ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                                            : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                                        boxShadow: (theme) => theme.palette.mode === 'dark'
                                            ? '0 6px 20px rgba(25, 118, 210, 0.5)'
                                            : '0 6px 20px rgba(25, 118, 210, 0.4)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                {unlocking ? 'Wird freigeschaltet...' : 'Hilfe für 10 Coins freischalten'}
                            </Button>
                            {unlockError && (
                                <Typography color="error" sx={{mt: 2}}>
                                    {unlockError}
                                </Typography>
                            )}
                            {userCoins !== null && userCoins < 10 && !unlockError && (
                                <Typography color="text.secondary" sx={{mt: 1}}>
                                    Du hast nicht genug Coins.
                                </Typography>
                            )}
                        </Box>
                    </Fade>
                ) : (
                    // Normal Chat Interface
                    <>
                        {error && (
                            <Alert
                                severity="error"
                                sx={{mb: 2}}
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={handleRetry}
                                        startIcon={<RefreshIcon/>}
                                    >
                                        Erneut versuchen
                                    </Button>
                                }
                            >
                                {error}
                            </Alert>
                        )}

                        <Box sx={{
                            flexGrow: 1,
                            overflowY: 'auto',
                            mb: 2,
                            pr: 1
                        }}>
                            {messages.map(renderMessage)}
                            <div ref={messagesEndRef}/>
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-end'
                        }}>
                            <TextField
                                fullWidth
                                multiline
                                maxRows={3}
                                placeholder="Stelle dem Tutor eine Frage..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={isStreaming}
                                size="small"
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'background.paper',
                                        '&:hover': {
                                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'grey.50',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'background.paper',
                                        }
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={sendMessage}
                                disabled={!inputValue.trim() || isStreaming}
                                sx={{minWidth: 44, height: 40}}
                            >
                                {isLoading ? (
                                    <CircularProgress size={20} color="inherit"/>
                                ) : (
                                    <SendIcon/>
                                )}
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ChatInterface;